import { useState, useEffect, useMemo } from 'react';
import api from '../../hooks/useApi';
import { formatMoney } from '../../utils/format';
import { formatHoursMinutes as hm } from '../../utils/overtime';
import * as XLSX from 'xlsx';

// ── Overtime (finance) ───────────────────────────────────────────────────────
//
// Overtime derived from the time log against the Overtime Policy, alongside
// the claims people have raised, on one ledger. The server builds it
// (src/lib/overtimeLedger.js); this page filters, totals and exports it.
//
// A row's status says how firm it is. Confirmed has an approved claim behind
// it. Pending is waiting in Approval Requests. Inferred was worked but never
// claimed: real hours, but nobody has said they want paying for them. Dismissed
// is the person saying it was not overtime.

const iso = (d) => d.toISOString().slice(0, 10);
const monthStartISO = (offset = 0) => { const d = new Date(); return iso(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + offset, 1))); };
const monthEndISO = (offset = 0) => { const d = new Date(); return iso(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + offset + 1, 0))); };
const quarterStartISO = () => { const d = new Date(); return iso(new Date(Date.UTC(d.getUTCFullYear(), Math.floor(d.getUTCMonth() / 3) * 3, 1))); };

const PRESETS = [
  { id: 'month',      label: 'This month',  range: () => [monthStartISO(0), iso(new Date())] },
  { id: 'last-month', label: 'Last month',  range: () => [monthStartISO(-1), monthEndISO(-1)] },
  { id: 'quarter',    label: 'This quarter', range: () => [quarterStartISO(), iso(new Date())] },
  { id: 'custom',     label: 'Custom',      range: null },
];

export const KIND_LABELS = {
  bank_holiday:     'Bank holiday (2x)',
  sunday:           'Sunday (2x)',
  saturday:         'Saturday (1.5x)',
  weekday_over_cap: 'Over daily cap (1.5x)',
  weekly_excess:    'Over weekly hours (1.5x)',
  standby:          'Standby (£2/h)',
  call_out:         'Call-out (2x, min 4h)',
  desk_move_weekday: 'Desk move, weekday (£15)',
  desk_move_weekend: 'Desk move, weekend (£20)',
  shift_move:       'Shift move (+25% uplift)',
  working_away:     'Working away (+25% uplift)',
  claimed_unlogged: 'Claimed, no time logged',
};

export const STATUS_LABELS = {
  confirmed: 'Confirmed',
  pending:   'Pending approval',
  rejected:  'Rejected',
  inferred:  'Inferred, not claimed',
  dismissed: 'Dismissed',
};

const STATUS_CLASS = {
  confirmed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  pending:   'bg-amber-50 text-amber-700 border-amber-200',
  rejected:  'bg-red-50 text-red-700 border-red-200',
  inferred:  'bg-orange-50 text-orange-700 border-orange-200',
  dismissed: 'bg-gray-100 text-gray-500 border-gray-200',
};

const ELIGIBILITY_LABELS = { paid: 'Paid', toil: 'TOIL', unknown: 'Grade not set', excluded: 'Not eligible' };

const fmtMoney = (v) => (v == null ? '—' : formatMoney(v, 2));
const fmtDate = (d) => (d ? new Date(`${d}T00:00:00Z`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }) : '—');
const fmtMonth = (m) => (m ? new Date(`${m}-01T00:00:00Z`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' }) : '—');

function StatusPill({ status }) {
  return (
    <span className={`inline-block px-1.5 py-0.5 text-[8px] font-bold uppercase rounded border whitespace-nowrap ${STATUS_CLASS[status] || STATUS_CLASS.dismissed}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

function Tile({ label, value, sub, tone = 'text-navy' }) {
  return (
    <div className="bg-white border border-gray-200 rounded p-2.5">
      <div className="text-[8px] text-gray-500 uppercase font-bold">{label}</div>
      <div className={`text-lg font-extrabold ${tone}`}>{value}</div>
      {sub && <div className="text-[9px] text-gray-400 mt-0.5">{sub}</div>}
    </div>
  );
}

const TH = ({ children, right }) => (
  <th className={`px-2 py-1.5 text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50 whitespace-nowrap ${right ? 'text-right' : 'text-left'}`}>{children}</th>
);

// ── The day drawer ───────────────────────────────────────────────────────────
//
// The table states a result; this says what produced it. A row carries the time
// entries behind it and the arithmetic the policy applied to them, so the whole
// day is here, overhead included. The SOW column can only name the references
// the hours were booked to, and it is usually the overhead that pushed somebody
// over the cap.
//
// The entries ride on the ledger row rather than being fetched: /time-entries
// gates on a role list, so a reader an administrator has granted this page but
// who holds none of those roles would open this onto a refusal.

function bookedTo(e) {
  if (e.sow_reference) return e.task_name ? `${e.sow_reference} · ${e.task_name}` : e.sow_reference;
  if (e.overhead_category_name) return `Overhead · ${e.overhead_category_name}`;
  return e.task_name || '—';
}

function Arithmetic({ basis, kind }) {
  if (!basis) return null;
  const parts = basis.cap_hours != null
    ? [`${hm(basis.logged_hours)} logged`, `${hm(basis.cap_hours)} daily cap`]
    : basis.contract_hours != null
      ? [`${hm(basis.logged_hours)} logged`, `${hm(basis.contract_hours)} contracted`, `${hm(basis.counted_hours)} already counted on daily rows`]
      : null;
  return (
    <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100 text-[10px] text-gray-600">
      {parts
        ? <>{parts.join(' - ')} = <span className="font-bold text-navy">{hm(basis.overtime_hours)}</span>
            {/* The margin gates the claim rather than reducing it, so it is
                said as the test the day passed, not as a subtraction. */}
            {basis.margin_hours != null && <span className="text-gray-500">, past the {hm(basis.margin_hours)} margin, so all of it counts</span>}
          </>
        : <>{hm(basis.logged_hours)} logged, and {KIND_LABELS[kind]?.toLowerCase() || 'this rate'} pays the whole day: <span className="font-bold text-navy">{hm(basis.overtime_hours)}</span></>}
    </div>
  );
}

// The claim as the approver saw it in Approvals, so finance need not go there
// to find who agreed it and what was asked for. Its hours are the claim's
// own, which can differ from the row's figure; the row states the policy's.
const CLAIM_TYPE_LABELS = {
  overtime:          'Overtime',
  standby:           'Standby',
  toil:              'TOIL',
  call_out:          'Call-Out',
  shift_move:        'Shift Move',
  working_away:      'Working Away',
  desk_move_weekday: 'Desk Move (Weekday)',
  desk_move_weekend: 'Desk Move (Weekend)',
  remote_working:    'Remote Working',
  out_of_hours:      'Out of Hours (>5PM / Sat)',
};

const CLAIM_STATUS_CLASS = {
  pending:  'bg-amber-50 text-amber-700 border-amber-200',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected: 'bg-red-50 text-red-700 border-red-200',
};

function fmtDatetime(s) {
  if (!s) return '—';
  const d = new Date(s.replace(' ', 'T'));
  if (isNaN(d)) return s;
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
}

function Field({ label, children, wide }) {
  return (
    <div className={wide ? 'col-span-2' : ''}>
      <div className="text-[8px] font-bold uppercase text-gray-500 mb-0.5">{label}</div>
      {children}
    </div>
  );
}

function ClaimPanel({ claim }) {
  if (!claim) return null;
  // A pending claim's approver is the manager it is waiting with, not
  // somebody who has decided it.
  const who = claim.approver_name
    ? claim.status === 'pending' ? `with ${claim.approver_name}` : `by ${claim.approver_name}`
    : null;
  return (
    <div className="px-4 py-3 border-b border-gray-100">
      <div className="grid grid-cols-2 gap-x-4 gap-y-2">
        <Field label="Type"><div className="text-[11px] font-semibold text-navy">{CLAIM_TYPE_LABELS[claim.request_type] || claim.request_type}</div></Field>
        <Field label="Status">
          <div className="flex items-center gap-2">
            <span className={`inline-block px-1.5 py-0.5 text-[8px] font-bold uppercase rounded border ${CLAIM_STATUS_CLASS[claim.status] || 'bg-gray-100 text-gray-500 border-gray-200'}`}>{claim.status}</span>
            {who && <span className="text-[9px] text-gray-500">{who}</span>}
          </div>
          {claim.approval_time && claim.status !== 'pending' && <div className="text-[8px] text-gray-400 mt-0.5">{fmtDatetime(claim.approval_time)}</div>}
        </Field>
        <Field label="Start"><div className="text-[10px]">{fmtDatetime(claim.start_datetime)}</div></Field>
        <Field label="End"><div className="text-[10px]">{fmtDatetime(claim.end_datetime)}</div></Field>
        <Field label="Duration claimed"><div className="text-[11px] font-semibold">{claim.hours != null ? hm(claim.hours) : '—'}</div></Field>
        {claim.reason && (
          <Field label="Description" wide><div className="text-[10px] text-gray-700 bg-gray-50 rounded p-2 whitespace-pre-wrap break-words">{claim.reason}</div></Field>
        )}
        {claim.status === 'rejected' && claim.rejection_reason && (
          <Field label="Rejection reason" wide><div className="text-[10px] text-red-700 bg-red-50 border border-red-200 rounded p-2 whitespace-pre-wrap break-words">{claim.rejection_reason}</div></Field>
        )}
      </div>
    </div>
  );
}

function DayDrawer({ row, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  if (!row) return null;

  const weekly = row.kind === 'weekly_excess';
  const logged = row.entries?.reduce((s, e) => s + (parseFloat(e.hours) || 0), 0) || 0;

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
      <div role="dialog" aria-modal="true" aria-label="Time logged behind this overtime"
        className="fixed top-0 right-0 bottom-0 w-full max-w-xl z-50 bg-white shadow-2xl flex flex-col">
        <div className="flex items-start gap-3 px-4 py-3 border-b border-gray-200 bg-gray-50/80 flex-shrink-0">
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-bold text-navy">{row.person_name}</div>
            <div className="text-[9px] text-gray-500">
              {weekly ? `Week of ${fmtDate(row.date)}` : fmtDate(row.date)} · {KIND_LABELS[row.kind] || row.kind}
              {row.team_name ? ` · ${row.team_name}` : ''}
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="text-sm font-extrabold text-navy">{hm(row.hours)}</div>
            <StatusPill status={row.status} />
          </div>
          <button onClick={onClose} aria-label="Close"
            className="w-6 h-6 rounded-full border border-gray-200 bg-white text-gray-400 hover:bg-gray-50 flex items-center justify-center text-[10px] flex-shrink-0">✕</button>
        </div>

        <Arithmetic basis={row.basis} kind={row.kind} />

        <ClaimPanel claim={row.claim} />

        {/* What the approver passed forward when they agreed it. Often the
            only thing that explains an unusual figure, so it sits with the
            figure rather than in the approver's own queue. */}
        {row.decision_note && (
          <div className="px-4 py-2.5 bg-blue-50 border-b border-blue-100">
            <div className="text-[8px] font-bold uppercase text-blue-700">Note from the approver</div>
            <div className="text-[10px] text-gray-700 mt-0.5 whitespace-pre-wrap break-words">{row.decision_note}</div>
          </div>
        )}

        <div className="flex-1 min-h-0 overflow-auto">
          {row.entries?.length ? (
            <table className="w-full">
              <thead><tr>
                {weekly && <TH>Date</TH>}<TH right>Hours</TH><TH>Booked to</TH><TH>Description</TH>
              </tr></thead>
              <tbody>
                {row.entries.map(e => (
                  <tr key={e.id} className="border-b border-gray-100 align-top">
                    {weekly && <td className="px-2 py-1.5 text-[9px] whitespace-nowrap">{fmtDate(e.date)}</td>}
                    <td className="px-2 py-1.5 text-[10px] font-semibold text-right whitespace-nowrap">{hm(e.hours)}</td>
                    <td className="px-2 py-1.5 text-[9px]">
                      {bookedTo(e)}
                      {e.billable === 0 && <span className="ml-1 text-[8px] text-gray-400">non-billable</span>}
                    </td>
                    <td className="px-2 py-1.5 text-[9px] text-gray-500">{e.description || '—'}</td>
                  </tr>
                ))}
                <tr className="bg-gray-50">
                  {weekly && <td className="px-2 py-1.5" />}
                  <td className="px-2 py-1.5 text-[10px] font-bold text-right text-navy">{hm(logged)}</td>
                  <td className="px-2 py-1.5 text-[9px] text-gray-500" colSpan={2}>
                    {row.entries.length} {weekly ? 'entries that week' : row.entries.length === 1 ? 'entry that day' : 'entries that day'}
                  </td>
                </tr>
              </tbody>
            </table>
          ) : (
            <div className="px-4 py-6 text-[10px] text-gray-500">
              No time log behind this one. {KIND_LABELS[row.kind] || row.kind} comes from a claim or the PLOW rather than from booked hours.
              {/* A claim's words are its description, already shown above. */}
              {row.description && !row.claim ? <div className="mt-2 text-gray-600">{row.description}</div> : null}
            </div>
          )}
        </div>

        <div className="px-4 py-2.5 border-t border-gray-200 bg-gray-50/80 text-[9px] text-gray-500 flex-shrink-0">
          {/* A weekly row's arithmetic has already said this, so it is not repeated here. */}
          {row.week_logged_hours != null && !weekly
            ? <>Week of {fmtDate(row.week_start)}: {hm(row.week_logged_hours)} logged against {hm(row.week_contract_hours)} contracted.{' '}</>
            : null}
          {row.hourly_rate != null && row.pay_cost != null
            ? <>{formatMoney(row.hourly_rate, 2)}/h × {row.multiplier} = {fmtMoney(row.pay_cost)}.</>
            : row.eligibility === 'toil' ? <>Grade {row.grade} earns time off in lieu rather than pay.</>
            : <>No salary on record, so this cannot be costed.</>}
        </div>
      </div>
    </>
  );
}

export default function OvertimeLedgerPage() {
  const [preset, setPreset] = useState('month');
  const [from, setFrom] = useState(() => PRESETS[0].range()[0]);
  const [to, setTo] = useState(() => PRESETS[0].range()[1]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [view, setView] = useState('ledger'); // ledger | person | sow | month
  const [statusFilter, setStatusFilter] = useState('');
  const [kindFilter, setKindFilter] = useState('');
  const [personFilter, setPersonFilter] = useState('');
  const [openKey, setOpenKey] = useState(null);

  const pickPreset = (id) => {
    setPreset(id);
    const p = PRESETS.find(x => x.id === id);
    if (p?.range) { const [f, t] = p.range(); setFrom(f); setTo(t); }
  };

  useEffect(() => {
    if (!from || !to || from > to) return;
    let cancelled = false;
    setLoading(true); setError('');
    api.get('overtime-ledger', { from, to })
      .then(d => { if (!cancelled) setData(d); })
      .catch(e => { if (!cancelled) setError(e.message || 'Could not load the ledger'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [from, to]);

  const rows = useMemo(() => (data?.rows || []).filter(r =>
    (!statusFilter || r.status === statusFilter) &&
    (!kindFilter || r.kind === kindFilter) &&
    (!personFilter || r.person_id === personFilter)
  ), [data, statusFilter, kindFilter, personFilter]);

  // Totals follow the filters, so a filtered table and its tiles agree.
  const totals = useMemo(() => {
    const t = { hours: 0, pay: 0, billed: 0, unclaimedHours: 0, unclaimedPay: 0, confirmedHours: 0, missingRate: 0 };
    for (const r of rows) {
      t.hours += r.hours; t.pay += r.pay_cost || 0; t.billed += r.billed_value || 0;
      if (r.status === 'inferred') { t.unclaimedHours += r.hours; t.unclaimedPay += r.pay_cost || 0; }
      if (r.status === 'confirmed') t.confirmedHours += r.hours;
      if (r.hourly_rate == null && r.eligibility !== 'toil' && r.rate_basis !== 'policy') t.missingRate++;
    }
    return t;
  }, [rows]);

  const byPerson = useMemo(() => {
    const m = new Map();
    for (const r of rows) {
      const p = m.get(r.person_id) || { person_id: r.person_id, person_name: r.person_name, team_name: r.team_name, grade: r.grade, eligibility: r.eligibility, rows: 0, hours: 0, unclaimed: 0, pay: 0, billed: 0 };
      p.rows++; p.hours += r.hours; p.pay += r.pay_cost || 0; p.billed += r.billed_value || 0;
      if (r.status === 'inferred') p.unclaimed += r.hours;
      m.set(r.person_id, p);
    }
    return [...m.values()].sort((a, b) => b.hours - a.hours);
  }, [rows]);

  const bySow = useMemo(() => {
    const m = new Map();
    for (const r of rows) {
      const splits = r.sow_split?.length ? r.sow_split : [{ sow_reference: null, hours: r.hours, billed_value: r.billed_value || 0 }];
      for (const s of splits) {
        const key = s.sow_reference || '__overhead__';
        const b = m.get(key) || { sow_reference: s.sow_reference, rows: 0, hours: 0, pay: 0, billed: 0 };
        b.rows++; b.hours += s.hours; b.billed += s.billed_value || 0;
        b.pay += r.pay_cost != null && r.hours > 0 ? r.pay_cost * (s.hours / r.hours) : 0;
        m.set(key, b);
      }
    }
    return [...m.values()].sort((a, b) => b.hours - a.hours);
  }, [rows]);

  const byMonth = useMemo(() => {
    const m = new Map();
    for (const r of rows) {
      const k = (r.date || '').slice(0, 7);
      const b = m.get(k) || { month: k, rows: 0, hours: 0, pay: 0, billed: 0, unclaimed: 0 };
      b.rows++; b.hours += r.hours; b.pay += r.pay_cost || 0; b.billed += r.billed_value || 0;
      if (r.status === 'inferred') b.unclaimed += r.hours;
      m.set(k, b);
    }
    return [...m.values()].sort((a, b) => a.month.localeCompare(b.month));
  }, [rows]);

  const people = data?.summary?.by_person || [];

  const exportXlsx = () => {
    const wb = XLSX.utils.book_new();
    const ledger = rows.map(r => ({
      Date: r.date, Person: r.person_name, Team: r.team_name || '', Grade: r.grade || '',
      Eligibility: ELIGIBILITY_LABELS[r.eligibility] || r.eligibility, Type: KIND_LABELS[r.kind] || r.kind,
      Hours: r.hours, Multiplier: r.multiplier, 'Hourly rate': r.hourly_rate ?? '', 'Pay cost': r.pay_cost ?? '',
      'Billed value': r.billed_value ?? '', SOWs: (r.sow_references || []).join(', '), Status: STATUS_LABELS[r.status] || r.status,
      Description: r.description || '', 'Approver note': r.decision_note || '',
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(ledger), 'Ledger');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(byPerson.map(p => ({ Person: p.person_name, Team: p.team_name || '', Grade: p.grade || '', Rows: p.rows, Hours: +p.hours.toFixed(2), 'Unclaimed hours': +p.unclaimed.toFixed(2), 'Pay cost': +p.pay.toFixed(2), 'Billed value': +p.billed.toFixed(2) }))), 'By person');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(bySow.map(s => ({ SOW: s.sow_reference || 'Overhead', Rows: s.rows, Hours: +s.hours.toFixed(2), 'Pay cost': +s.pay.toFixed(2), 'Billed value': +s.billed.toFixed(2) }))), 'By SOW');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(byMonth.map(m => ({ Month: m.month, Rows: m.rows, Hours: +m.hours.toFixed(2), 'Unclaimed hours': +m.unclaimed.toFixed(2), 'Pay cost': +m.pay.toFixed(2), 'Billed value': +m.billed.toFixed(2) }))), 'By month');
    XLSX.writeFile(wb, `Overtime_${from}_to_${to}.xlsx`);
  };

  const views = [['ledger', 'Ledger'], ['person', 'By person'], ['sow', 'By SOW'], ['month', 'By month']];
  const confirmedShare = totals.hours > 0 ? Math.round((totals.confirmedHours / totals.hours) * 100) : 0;

  return (
    <div className="fade-in">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div>
          <h2 className="text-base font-extrabold text-navy">Overtime</h2>
          <div className="text-[9px] text-gray-500">Overtime derived from the time log against the Overtime Policy, alongside the claims raised. Confirmed rows have an approved claim behind them; inferred rows were worked but not claimed.</div>
        </div>
        <button onClick={exportXlsx} disabled={!rows.length}
          className="px-3 py-1.5 bg-navy text-white text-[10px] font-semibold rounded hover:bg-navy/80 disabled:opacity-40">
          Export XLSX
        </button>
      </div>

      {/* Range */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="flex gap-1">
          {PRESETS.map(p => (
            <button key={p.id} onClick={() => pickPreset(p.id)}
              className={`px-2.5 py-1 text-[10px] font-semibold rounded ${preset === p.id ? 'bg-navy text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
              {p.label}
            </button>
          ))}
        </div>
        <input type="date" value={from} onChange={e => { setPreset('custom'); setFrom(e.target.value); }} className="form-input text-[10px] py-1" style={{ width: "auto", display: "inline-block" }} />
        <span className="text-[10px] text-gray-400">to</span>
        <input type="date" value={to} onChange={e => { setPreset('custom'); setTo(e.target.value); }} className="form-input text-[10px] py-1" style={{ width: "auto", display: "inline-block" }} />
        {loading && <span className="text-[10px] text-gray-400">Loading…</span>}
        {error && <span className="text-[10px] text-red-600">{error}</span>}
      </div>

      {/* Tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mb-3">
        <Tile label="Overtime hours" value={hm(totals.hours)} sub={`${rows.length} row${rows.length === 1 ? '' : 's'}`} />
        <Tile label="Pay cost" value={fmtMoney(totals.pay)} sub={totals.missingRate ? `${totals.missingRate} row${totals.missingRate === 1 ? '' : 's'} without a salary on record` : 'Salary-based, at policy rates'} />
        <Tile label="Billed value" value={fmtMoney(totals.billed)} sub="What the SOWs charge for these hours" />
        <Tile label="Unclaimed" value={hm(totals.unclaimedHours)} sub={totals.unclaimedHours ? `${fmtMoney(totals.unclaimedPay)} if all claimed` : 'Nothing worked but unclaimed'} tone={totals.unclaimedHours ? 'text-orange-600' : 'text-navy'} />
        <Tile label="Confirmed" value={`${confirmedShare}%`} sub="Share of hours with an approved claim" tone={confirmedShare === 100 ? 'text-emerald-600' : 'text-navy'} />
      </div>

      {/* Views + filters */}
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="flex gap-1">
          {views.map(([id, label]) => (
            <button key={id} onClick={() => setView(id)}
              className={`px-3 py-1 text-[10px] font-semibold rounded ${view === id ? 'bg-navy text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="flex gap-1.5 flex-wrap">
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="form-select text-[10px] py-1" style={{ width: "auto", display: "inline-block" }}>
            <option value="">All statuses</option>
            {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select value={kindFilter} onChange={e => setKindFilter(e.target.value)} className="form-select text-[10px] py-1" style={{ width: "auto", display: "inline-block" }}>
            <option value="">All types</option>
            {Object.entries(KIND_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select value={personFilter} onChange={e => setPersonFilter(e.target.value)} className="form-select text-[10px] py-1" style={{ width: "auto", display: "inline-block" }}>
            <option value="">Everyone</option>
            {people.map(p => <option key={p.person_id} value={p.person_id}>{p.person_name}</option>)}
          </select>
        </div>
      </div>

      {view === 'ledger' && (
        <div className="bg-white border border-gray-200 rounded overflow-x-auto">
          <table className="w-full">
            <thead><tr>
              <TH>Date</TH><TH>Person</TH><TH>Team</TH><TH>Type</TH><TH right>Hours</TH><TH right>Rate</TH><TH right>Pay cost</TH><TH right>Billed</TH><TH>SOW</TH><TH>Status</TH><TH>Description</TH>
            </tr></thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.key} className="hover:bg-gold/5 border-b border-gray-100 cursor-pointer"
                  onClick={() => setOpenKey(r.key)} title="See the time logged and the claim behind this">
                  <td className="px-2 py-1.5 text-[9px] whitespace-nowrap">{r.kind === 'weekly_excess' ? `Week of ${fmtDate(r.date)}` : fmtDate(r.date)}</td>
                  <td className="px-2 py-1.5 text-[10px] font-semibold text-navy whitespace-nowrap">{r.person_name}
                    <div className="text-[8px] text-gray-400 font-normal">{ELIGIBILITY_LABELS[r.eligibility] || r.eligibility}{r.grade ? ` · Grade ${r.grade}` : ''}</div>
                  </td>
                  <td className="px-2 py-1.5 text-[9px] text-gray-500 whitespace-nowrap">{r.team_name || '—'}</td>
                  <td className="px-2 py-1.5 text-[9px] whitespace-nowrap">{KIND_LABELS[r.kind] || r.kind}</td>
                  <td className="px-2 py-1.5 text-[10px] font-semibold text-right whitespace-nowrap">{hm(r.hours)}</td>
                  <td className="px-2 py-1.5 text-[9px] text-right text-gray-500 whitespace-nowrap">{r.hourly_rate != null ? `${formatMoney(r.hourly_rate, 2)}/h × ${r.multiplier}` : r.rate_basis === 'policy' ? 'fixed allowance' : <span className="text-orange-600">no salary</span>}</td>
                  <td className="px-2 py-1.5 text-[10px] text-right whitespace-nowrap">{r.pay_cost != null ? fmtMoney(r.pay_cost) : r.toil_days != null ? <span className="text-gray-400" title="Time off in lieu rather than pay">TOIL {r.toil_days}d</span> : r.eligibility === 'toil' ? <span className="text-gray-400">TOIL</span> : '—'}</td>
                  <td className="px-2 py-1.5 text-[10px] text-right whitespace-nowrap">{r.billed_value ? fmtMoney(r.billed_value) : '—'}</td>
                  <td className="px-2 py-1.5 text-[9px] whitespace-nowrap">{r.sow_references?.length ? r.sow_references.join(', ') : <span className="text-gray-400">Overhead</span>}</td>
                  <td className="px-2 py-1.5"><StatusPill status={r.status} /></td>
                  <td className="px-2 py-1.5 text-[9px] text-gray-500 max-w-[220px] truncate" title={r.decision_note ? `${r.description || ''}\n\nNote from the approver: ${r.decision_note}` : (r.description || '')}>
                    {r.decision_note && (
                      <span className="mr-1 text-[8px] font-bold text-blue-700 bg-blue-50 border border-blue-100 px-1 rounded"
                        title="The approver left a note — open the row to read it">note</span>
                    )}
                    {r.description || '—'}
                  </td>
                </tr>
              ))}
              {!rows.length && !loading && (
                <tr><td colSpan={11} className="text-center py-8 text-[10px] text-gray-400">No overtime in this range{statusFilter || kindFilter || personFilter ? ' with these filters' : ''}.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {view === 'person' && (
        <div className="bg-white border border-gray-200 rounded overflow-x-auto">
          <table className="w-full">
            <thead><tr><TH>Person</TH><TH>Team</TH><TH>Eligibility</TH><TH right>Rows</TH><TH right>Hours</TH><TH right>Unclaimed</TH><TH right>Pay cost</TH><TH right>Billed</TH></tr></thead>
            <tbody>
              {byPerson.map(p => (
                <tr key={p.person_id} className="hover:bg-gold/5 border-b border-gray-100 cursor-pointer" onClick={() => { setPersonFilter(p.person_id); setView('ledger'); }}>
                  <td className="px-2 py-1.5 text-[10px] font-semibold text-navy whitespace-nowrap">{p.person_name}</td>
                  <td className="px-2 py-1.5 text-[9px] text-gray-500">{p.team_name || '—'}</td>
                  <td className="px-2 py-1.5 text-[9px]">{ELIGIBILITY_LABELS[p.eligibility] || p.eligibility}{p.grade ? ` (Grade ${p.grade})` : ''}</td>
                  <td className="px-2 py-1.5 text-[10px] text-right">{p.rows}</td>
                  <td className="px-2 py-1.5 text-[10px] font-semibold text-right">{hm(p.hours)}</td>
                  <td className={`px-2 py-1.5 text-[10px] text-right ${p.unclaimed ? 'text-orange-600 font-semibold' : ''}`}>{hm(p.unclaimed)}</td>
                  <td className="px-2 py-1.5 text-[10px] text-right">{p.pay ? fmtMoney(p.pay) : p.eligibility === 'toil' ? <span className="text-gray-400">TOIL</span> : '—'}</td>
                  <td className="px-2 py-1.5 text-[10px] text-right">{p.billed ? fmtMoney(p.billed) : '—'}</td>
                </tr>
              ))}
              {!byPerson.length && <tr><td colSpan={8} className="text-center py-8 text-[10px] text-gray-400">No overtime in this range.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {view === 'sow' && (
        <div className="bg-white border border-gray-200 rounded overflow-x-auto">
          <table className="w-full">
            <thead><tr><TH>SOW</TH><TH right>Rows</TH><TH right>Hours</TH><TH right>Pay cost</TH><TH right>Billed value</TH><TH right>Billed less pay</TH></tr></thead>
            <tbody>
              {bySow.map(s => {
                const margin = s.billed - s.pay;
                return (
                  <tr key={s.sow_reference || '__overhead__'} className="hover:bg-gold/5 border-b border-gray-100">
                    <td className="px-2 py-1.5 text-[10px] font-semibold text-navy whitespace-nowrap">{s.sow_reference || <span className="text-gray-400 font-normal">Overhead (no SOW)</span>}</td>
                    <td className="px-2 py-1.5 text-[10px] text-right">{s.rows}</td>
                    <td className="px-2 py-1.5 text-[10px] font-semibold text-right">{hm(s.hours)}</td>
                    <td className="px-2 py-1.5 text-[10px] text-right">{fmtMoney(s.pay)}</td>
                    <td className="px-2 py-1.5 text-[10px] text-right">{s.billed ? fmtMoney(s.billed) : '—'}</td>
                    <td className={`px-2 py-1.5 text-[10px] text-right font-semibold ${margin < 0 ? 'text-red-600' : margin > 0 ? 'text-emerald-600' : 'text-gray-400'}`}>{s.billed || s.pay ? fmtMoney(margin) : '—'}</td>
                  </tr>
                );
              })}
              {!bySow.length && <tr><td colSpan={6} className="text-center py-8 text-[10px] text-gray-400">No overtime in this range.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {view === 'month' && (
        <div className="bg-white border border-gray-200 rounded overflow-x-auto">
          <table className="w-full">
            <thead><tr><TH>Month</TH><TH right>Rows</TH><TH right>Hours</TH><TH right>Unclaimed</TH><TH right>Pay cost</TH><TH right>Billed value</TH></tr></thead>
            <tbody>
              {byMonth.map(m => (
                <tr key={m.month} className="hover:bg-gold/5 border-b border-gray-100">
                  <td className="px-2 py-1.5 text-[10px] font-semibold text-navy whitespace-nowrap">{fmtMonth(m.month)}</td>
                  <td className="px-2 py-1.5 text-[10px] text-right">{m.rows}</td>
                  <td className="px-2 py-1.5 text-[10px] font-semibold text-right">{hm(m.hours)}</td>
                  <td className={`px-2 py-1.5 text-[10px] text-right ${m.unclaimed ? 'text-orange-600 font-semibold' : ''}`}>{hm(m.unclaimed)}</td>
                  <td className="px-2 py-1.5 text-[10px] text-right">{fmtMoney(m.pay)}</td>
                  <td className="px-2 py-1.5 text-[10px] text-right">{m.billed ? fmtMoney(m.billed) : '—'}</td>
                </tr>
              ))}
              {!byMonth.length && <tr><td colSpan={6} className="text-center py-8 text-[10px] text-gray-400">No overtime in this range.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      <DayDrawer row={rows.find(r => r.key === openKey) || null} onClose={() => setOpenKey(null)} />

      <div className="mt-3 text-[9px] text-gray-400 leading-relaxed">
        Click any row to see the time logged behind it and, where one has been raised, the claim as Approvals shows it.
        How rows are derived: Sunday and bank holiday hours at 2x, Saturday hours at 1.5x, every weekday hour over the person's daily cap at 1.5x once the day runs more than 20 minutes past it, and a week over contractual hours picks up anything the daily rows have not counted, at 1.5x. The 20 minutes decides whether a day is claimable, it is not taken off a claim that clears it.
        A row marked <em>note</em> carries something the approver said when they agreed the claim; open the row to read it. Pay cost is salary over 52 weeks of contractual hours, times the multiplier. Billed value is what the SOW charges for the same hours. Grades 1 and 2 earn TOIL rather than pay; grades 3 and 4 are paid; a person with no grade is costed but flagged. Sub-contractors and people who cannot claim overtime are excluded.
        Time entries have no clock times, so evening work on a normal-length day cannot be seen here. People claim from My Info, Time &amp; Allowances, and approvals happen in Approval Requests.
      </div>
    </div>
  );
}
